import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import StaffCalendar from "../components/StaffCalendar";
import "../static/staff-hr.css";

const todayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const keyOf = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const calendarRange = (visibleMonth) => {
  const firstDay = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1
  );

  const mondayOffset = (firstDay.getDay() + 6) % 7;

  const start = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1 - mondayOffset
  );

  const end = new Date(start);
  end.setDate(start.getDate() + 41);

  return {
    start: keyOf(start),
    end: keyOf(end),
  };
};

const blankStaff = () => ({
  name: "",
  phone: "",
  email: "",
  status: "3",
  employment_type: "full_time",
  hire_date: todayKey(),
});

const StaffHR = () => {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [assignments, setAssignments] = useState([]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [staffForm, setStaffForm] = useState(blankStaff());

  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [selectedDate, setSelectedDate] = useState("");
  const [hoursWorked, setHoursWorked] = useState("8");

  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedStaff = staffList.find(
    (staff) => String(staff.id) === String(selectedStaffId)
  );

  const range = useMemo(
    () => calendarRange(visibleMonth),
    [visibleMonth]
  );

  const monthAssignments = useMemo(() => {
    return [...assignments].sort((a, b) =>
      b.date.localeCompare(a.date)
    );
  }, [assignments]);

  const summary = useMemo(() => {
    const worked = assignments.filter(
      (item) => item.attendance_status === "present"
    );

    return {
      days: worked.length,
      fullDays: worked.filter(
        (item) => item.shift_type === "full_time"
      ).length,
      partDays: worked.filter(
        (item) => item.shift_type === "part_time"
      ).length,
      hours: worked.reduce(
        (total, item) => total + Number(item.hours_worked || 0),
        0
      ),
    };
  }, [assignments]);

  const loadStaff = async () => {
    try {
      const response = await axios.get("/petgrooming/staff-hr/");
      const result = Array.isArray(response.data) ? response.data : [];

      setStaffList(result);

      if (!selectedStaffId && result.length > 0) {
        setSelectedStaffId(String(result[0].id));
      }
    } catch (error) {
      console.error(error.response?.data || error);
      setMessage(
        `Could not load staff: ${JSON.stringify(
          error.response?.data || error.message
        )}`
      );
    }
  };

  const loadAssignments = async () => {
    if (!selectedStaffId) {
      setAssignments([]);
      return;
    }

    try {
      const response = await axios.get("/petgrooming/staff-hr/assignments/", {
        params: {
          staff_id: selectedStaffId,
          start: range.start,
          end: range.end,
        },
      });

      setAssignments(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      console.error(error.response?.data || error);
      setMessage(
        `Could not load calendar: ${JSON.stringify(
          error.response?.data || error.message
        )}`
      );
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [selectedStaffId, range.start, range.end]);

  const updateStaffForm = (event) => {
    const { name, value } = event.target;

    setStaffForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const saveStaff = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      const payload = {
        name: staffForm.name.trim(),
        phone: staffForm.phone.trim(),
        email: staffForm.email.trim(),
        status: Number(staffForm.status),
        employment_type: staffForm.employment_type,
        hire_date: staffForm.hire_date,
      };

      const response = await axios.post("/petgrooming/staff-hr/", payload);

      setMessage(`${response.data.name} added successfully.`);
      setShowAddForm(false);
      setStaffForm(blankStaff());

      await loadStaff();
      setSelectedStaffId(String(response.data.id));
    } catch (error) {
      console.error(error.response?.data || error);

      setMessage(
        `Could not save staff: ${JSON.stringify(
          error.response?.data || error.message
        )}`
      );
    } finally {
      setSaving(false);
    }
  };

  const openDate = (date, assignment) => {
    if (!selectedStaff) return;

    setSelectedDate(date);

    setHoursWorked(
      String(
        assignment?.hours_worked ??
          (selectedStaff.employment_type === "part_time" ? 4 : 8)
      )
    );
  };

  const saveWorkDay = async (event) => {
    event.preventDefault();

    if (!selectedStaffId || !selectedDate || !selectedStaff) return;

    setSaving(true);
    setMessage("");

    try {
      await axios.post("/petgrooming/staff-hr/assignments/", {
        staff_id: selectedStaffId,
        date: selectedDate,
        attendance_status: "present",
        shift_type: selectedStaff.employment_type,
        hours_worked: hoursWorked,
        dog_grooming: 0,
        cat_grooming: 0,
        notes: "",
      });

      setMessage(`Work day saved: ${selectedDate}`);
      setSelectedDate("");
      await loadAssignments();
    } catch (error) {
      console.error(error.response?.data || error);

      setMessage(
        `Could not save work day: ${JSON.stringify(
          error.response?.data || error.message
        )}`
      );
    } finally {
      setSaving(false);
    }
  };

  const removeWorkDay = async () => {
    if (!selectedStaffId || !selectedDate) return;

    const approved = window.confirm(
      `Remove ${selectedDate} from this staff calendar?`
    );

    if (!approved) return;

    setSaving(true);
    setMessage("");

    try {
      await axios.delete("/petgrooming/staff-hr/assignments/", {
        data: {
          staff_id: selectedStaffId,
          date: selectedDate,
        },
      });

      setMessage(`Work day removed: ${selectedDate}`);
      setSelectedDate("");
      await loadAssignments();
    } catch (error) {
      console.error(error.response?.data || error);

      setMessage(
        `Could not remove work day: ${JSON.stringify(
          error.response?.data || error.message
        )}`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="simple-staff-page">
      <header className="simple-staff-hero">
        <div>
          <span>Arys Grooming</span>
          <h1>Staff calendar</h1>
          <p>Add staff, then click calendar dates to add or remove work days.</p>
        </div>

        <button
          type="button"
          onClick={() => {
            setShowAddForm((current) => !current);
            setMessage("");
          }}
        >
          {showAddForm ? "Close form" : "+ Add staff"}
        </button>
      </header>

      {message && (
        <div className="simple-staff-message">{message}</div>
      )}

      {showAddForm && (
        <section className="simple-add-staff-box">
          <div className="simple-box-head">
            <div>
              <span>New staff member</span>
              <h2>Add staff member</h2>
            </div>
          </div>

          <form onSubmit={saveStaff} className="simple-staff-form">
            <label>
              Full name
              <input
                name="name"
                value={staffForm.name}
                onChange={updateStaffForm}
                required
              />
            </label>

            <label>
              Phone
              <input
                name="phone"
                value={staffForm.phone}
                onChange={updateStaffForm}
                required
              />
            </label>

            <label>
              Email
              <input
                type="email"
                name="email"
                value={staffForm.email}
                onChange={updateStaffForm}
                required
              />
            </label>

            <label>
              Role
              <select
                name="status"
                value={staffForm.status}
                onChange={updateStaffForm}
              >
                <option value="1">Daily Job</option>
                <option value="2">Assistant</option>
                <option value="3">Groomer</option>
                <option value="4">Manager</option>
              </select>
            </label>

            <label>
              Employment type
              <select
                name="employment_type"
                value={staffForm.employment_type}
                onChange={updateStaffForm}
              >
                <option value="full_time">Full time</option>
                <option value="part_time">Part time</option>
              </select>
            </label>

            <label>
              Hire date
              <input
                type="date"
                name="hire_date"
                value={staffForm.hire_date}
                onChange={updateStaffForm}
                required
              />
            </label>

            <button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Add staff member"}
            </button>
          </form>
        </section>
      )}

      <section className="simple-staff-layout">
        <aside className="simple-staff-list">
          <h2>Staff</h2>

          {staffList.length ? (
            staffList.map((staff) => (
              <button
                type="button"
                key={staff.id}
                className={
                  String(staff.id) === String(selectedStaffId)
                    ? "active"
                    : ""
                }
                onClick={() => {
                  setSelectedStaffId(String(staff.id));
                  setSelectedDate("");
                }}
              >
                <strong>{staff.name}</strong>
                <span>
                  {staff.employment_type === "part_time"
                    ? "Part time"
                    : "Full time"}
                </span>
              </button>
            ))
          ) : (
            <p>No staff added yet.</p>
          )}
        </aside>

        <section className="simple-calendar-box">
          {!selectedStaff ? (
            <div className="simple-empty-calendar">
              Add a staff member to open the calendar.
            </div>
          ) : (
            <>
              <header className="selected-staff-head">
                <div>
                  <span>Selected staff</span>
                  <h2>{selectedStaff.name}</h2>
                  <p>
                    {selectedStaff.employment_type === "part_time"
                      ? "Part time"
                      : "Full time"}{" "}
                    · Hired {selectedStaff.hire_date}
                  </p>
                </div>
              </header>

              <div className="simple-month-report">
                <article>
                  <span>Worked days</span>
                  <strong>{summary.days}</strong>
                </article>

                <article>
                  <span>Full time</span>
                  <strong>{summary.fullDays}</strong>
                </article>

                <article>
                  <span>Part time</span>
                  <strong>{summary.partDays}</strong>
                </article>

                <article>
                  <span>Total hours</span>
                  <strong>{summary.hours}</strong>
                </article>
              </div>

              <StaffCalendar
                visibleMonth={visibleMonth}
                assignments={assignments}
                onPrevious={() =>
                  setVisibleMonth(
                    (current) =>
                      new Date(
                        current.getFullYear(),
                        current.getMonth() - 1,
                        1
                      )
                  )
                }
                onNext={() =>
                  setVisibleMonth(
                    (current) =>
                      new Date(
                        current.getFullYear(),
                        current.getMonth() + 1,
                        1
                      )
                  )
                }
                onToday={() => {
                  const now = new Date();
                  setVisibleMonth(
                    new Date(now.getFullYear(), now.getMonth(), 1)
                  );
                  openDate(todayKey(), null);
                }}
                onSelectDay={openDate}
              />

              {selectedDate && (
                <form className="simple-workday-form" onSubmit={saveWorkDay}>
                  <div>
                    <span>Working day</span>
                    <h2>{selectedDate}</h2>
                  </div>

                  <label>
                    Hours worked
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={hoursWorked}
                      onChange={(event) =>
                        setHoursWorked(event.target.value)
                      }
                      required
                    />
                  </label>

                  <button type="submit" disabled={saving}>
                    Save working day
                  </button>

                  <button
                    type="button"
                    className="remove-day"
                    onClick={removeWorkDay}
                    disabled={saving}
                  >
                    Remove day
                  </button>
                </form>
              )}

              <section className="simple-worked-dates">
                <h2>Worked dates this month</h2>

                {monthAssignments.length ? (
                  monthAssignments.map((item) => (
                    <article key={item.id}>
                      <strong>{item.date}</strong>
                      <span>
                        {item.shift_type === "part_time"
                          ? "Part time"
                          : "Full time"}{" "}
                        · {item.hours_worked} hours
                      </span>
                    </article>
                  ))
                ) : (
                  <p>No working day recorded in this month.</p>
                )}
              </section>
            </>
          )}
        </section>
      </section>
    </main>
  );
};

export default StaffHR;
